// Global
import { tv } from 'tailwind-variants';

// Local
import { Lists } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import useIsEditing from 'lib/hooks/useIsEditing';

export type CarouselItemProps = Lists.CarouselItem_Component;

const CarouselItem = (props: CarouselItemProps): JSX.Element => {
  const { description, image, primaryCTA, secondaryCTA, title } = props?.fields || {};
  const isEditing = useIsEditing();

  /*
   * Rendering
   */

  const {
    content,
    cta,
    ctaButtons,
    descriptionText,
    heading,
    imageWrapper,
    slide,
    slideContent,
    slideMedia,
    wrapper,
  } = TAILWIND_VARIANTS();

  const styles = parseStyleParams(props.params, ['cta1', 'cta2']);

  return (
    <div className={slide()} data-component="authorable/shared/lists/carouselitem">
      <div className={slideContent()}>
        <div className={content()}>
          <div className={wrapper()}>
            {(title?.value !== '' || isEditing) && (
              <h2 className={heading()}>
                <PlainTextWrapper field={title} />
              </h2>
            )}

            {description && <RichTextWrapper className={descriptionText()} field={description} />}
            {primaryCTA && (
              <div className={ctaButtons()}>
                <LinkWrapper
                  aria-label={primaryCTA?.value.text}
                  className={cta({ style: styles.cta1?.ctaVariant })}
                  ctaStyle={getCtaStyle(styles.cta1, 'primary')}
                  field={primaryCTA}
                ></LinkWrapper>
                <LinkWrapper
                  aria-label={secondaryCTA?.value.text}
                  className={cta({ style: styles.cta2?.ctaVariant })}
                  ctaStyle={getCtaStyle(styles.cta2, 'secondary')}
                  field={secondaryCTA}
                ></LinkWrapper>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={slideMedia()}>
        <ImageWrapper className={imageWrapper()} field={image} />
      </div>
    </div>
  );
};

export const Default = withStandardComponentWrapper(CarouselItem);

const TAILWIND_VARIANTS = tv({
  slots: {
    content: ['slide-content-inner', 'flex', 'items-center', 'justify-start'],
    cta: [],
    descriptionText: ['mb-4', 'text-base', 'text-theme-black-800', 'sm:text-lg'],
    ctaButtons: ['flex', 'flex-col', 'gap-3', 'items-center', 'lg:flex-row'],
    heading: [
      'capitalize',
      'font-bold',
      'mb-4',
      'text-2xl',
      'lg:text-4xl',
      'lg:leading-[48px]',
      'text-theme-black-800',
    ],
    imageWrapper: ['h-full', 'object-cover', 'w-full'],
    slide: ['flex', 'items-center', 'justify-center', 'min-h-125'],
    slideContent: [
      'slide-content',
      'absolute',
      'max-w-[70%]',
      'z-10',
      'lg:box-border',
      'lg:left-[10%]',
      'lg:max-w-[70%]',
      'lg:top-1/2 ',
      'lg:transform',
      'lg:-translate-y-1/2',
      'lg:p-5',
      'lg:text-left',
      'xl:max-w-[50%]',
    ],
    slideMedia: [
      'slide-media',
      'absolute',
      'bg-center',
      'bg-cover',
      'h-full',
      'left-0',
      'top-0',
      'w-full',
      'lg:max-h-125',
    ],
    wrapper: ['bg-neutral-50', 'p-4', 'relative', 'text-black', 'lg:p-6'],
  },
  variants: {
    style: {
      link: {
        cta: ['text-base', 'text-theme-brand3'],
      },
      primary: {
        cta: ['px-4'],
      },
      secondary: {
        cta: ['px-4'],
      },
      secondarySmall: {
        cta: ['px-4'],
      },
      tertiary: {
        cta: ['px-4'],
      },
      hyperlink: {
        cta: ['px-4'],
      },
      jumplink: {
        cta: ['px-4'],
      },
    },
  },
});
